import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, Clock, Users, Ban, MessageSquareOff } from 'lucide-react';
import { toast } from 'sonner';

interface AdvancedModerationProps {
  streamId?: string;
}

export const AdvancedModeration = ({ streamId }: AdvancedModerationProps) => {
  const [slowMode, setSlowMode] = useState(false);
  const [slowModeDelay, setSlowModeDelay] = useState(5);
  const [subscribersOnly, setSubscribersOnly] = useState(false);
  const [followersOnly, setFollowersOnly] = useState(false);
  const [followDuration, setFollowDuration] = useState(10);
  const [autoModEnabled, setAutoModEnabled] = useState(true);
  const [bannedWords, setBannedWords] = useState<string[]>(['spam', 'scam']);
  const [newWord, setNewWord] = useState('');

  const handleAddBannedWord = () => {
    if (newWord.trim() && !bannedWords.includes(newWord.toLowerCase())) {
      setBannedWords([...bannedWords, newWord.toLowerCase()]);
      setNewWord('');
      toast.success('Word added to filter');
    }
  };

  const handleRemoveBannedWord = (word: string) => {
    setBannedWords(bannedWords.filter(w => w !== word));
    toast.success('Word removed from filter');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Advanced Moderation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Slow Mode */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Slow Mode
              </Label>
              <p className="text-xs text-muted-foreground">
                Limit chat message frequency
              </p>
            </div>
            <Switch checked={slowMode} onCheckedChange={setSlowMode} />
          </div>
          {slowMode && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                max="120"
                value={slowModeDelay}
                onChange={(e) => setSlowModeDelay(Number(e.target.value))}
                className="w-20"
              />
              <span className="text-sm text-muted-foreground">seconds between messages</span>
            </div>
          )}
        </div>

        {/* Subscribers Only */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Subscribers Only Chat
            </Label>
            <p className="text-xs text-muted-foreground">
              Only subscribers can send messages
            </p>
          </div>
          <Switch checked={subscribersOnly} onCheckedChange={setSubscribersOnly} />
        </div>

        {/* Followers Only */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Followers Only Chat</Label>
              <p className="text-xs text-muted-foreground">
                Require minimum follow duration
              </p>
            </div>
            <Switch checked={followersOnly} onCheckedChange={setFollowersOnly} />
          </div>
          {followersOnly && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="60"
                value={followDuration}
                onChange={(e) => setFollowDuration(Number(e.target.value))}
                className="w-20"
              />
              <span className="text-sm text-muted-foreground">minutes following required</span>
            </div>
          )}
        </div>

        {/* Auto-Mod */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="flex items-center gap-2">
              <MessageSquareOff className="h-4 w-4" />
              Auto-Moderation
            </Label>
            <p className="text-xs text-muted-foreground">
              Automatically filter spam and toxic messages
            </p>
          </div>
          <Switch checked={autoModEnabled} onCheckedChange={setAutoModEnabled} />
        </div>

        {/* Banned Words */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Ban className="h-4 w-4" />
            Banned Words Filter
          </Label>
          <div className="flex gap-2">
            <Input
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Add word to filter"
              onKeyPress={(e) => e.key === 'Enter' && handleAddBannedWord()}
            />
            <Button onClick={handleAddBannedWord} variant="outline">
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {bannedWords.map((word) => (
              <Badge
                key={word}
                variant="secondary"
                className="cursor-pointer hover:bg-destructive"
                onClick={() => handleRemoveBannedWord(word)}
              >
                {word} ×
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
